import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, AlertTriangle, Users } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/app/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/app/components/ui/dialog';
import { cn } from '@/app/components/ui/utils';
import { useTechProfiles } from '@/hooks/use-crew';
import { useProjects } from '@/hooks/use-projects';
import { usePlatformModules } from '@/hooks/use-modules';
import { useAdminInvite } from '@/hooks/use-admin';
import type { TechProfileListItem, TechCertType, TechStatus, ModuleRoleLevel } from '@/lib/types';
import {
  getCertStatusDisplay,
  getIrataLevel,
} from './people-certification-display';

const statusColors: Record<string, string> = {
  active: 'bg-green-100 text-green-800',
  inactive: 'bg-gray-100 text-gray-800',
  on_leave: 'bg-yellow-100 text-yellow-800',
  terminated: 'bg-red-100 text-red-800',
};

export function PeopleTab() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [irataFilter, setIrataFilter] = useState<string>('');
  const [page, setPage] = useState(1);
  const [inviteOpen, setInviteOpen] = useState(false);

  const filters: {
    q?: string;
    status?: TechStatus | '';
    cert_type?: TechCertType | '';
    page: number;
    page_size: number;
  } = { page, page_size: 25 };
  if (search) filters.q = search;
  if (statusFilter) filters.status = statusFilter as TechStatus;
  if (irataFilter) filters.cert_type = irataFilter as TechCertType;

  const { data, isLoading, isError } = useTechProfiles(filters);

  useEffect(() => {
    if (isError) {
      toast.error('Failed to load people');
    }
  }, [isError]);

  const handleRowClick = useCallback(
    (tech: TechProfileListItem) => {
      navigate(`/people/${tech.id}`);
    },
    [navigate]
  );

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="flex flex-col sm:flex-row sm:flex-1 gap-2 sm:gap-3 sm:items-center">
          <div className="relative w-full sm:flex-1 sm:min-w-[200px] sm:max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <Select
            value={statusFilter}
            onValueChange={(v) => {
              setStatusFilter(v === 'all' ? '' : v);
              setPage(1);
            }}
          >
            <SelectTrigger className="w-full sm:w-[140px]" size="sm">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
              <SelectItem value="on_leave">On Leave</SelectItem>
              <SelectItem value="terminated">Terminated</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={irataFilter}
            onValueChange={(v) => {
              setIrataFilter(v === 'all' ? '' : v);
              setPage(1);
            }}
          >
            <SelectTrigger className="w-full sm:w-[160px]" size="sm">
              <SelectValue placeholder="IRATA Level" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Levels</SelectItem>
              <SelectItem value="irata_1">IRATA Level 1</SelectItem>
              <SelectItem value="irata_2">IRATA Level 2</SelectItem>
              <SelectItem value="irata_3">IRATA Level 3</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <button
          onClick={() => setInviteOpen(true)}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="h-4 w-4" />
          Invite Employee
        </button>
      </div>

      {/* Table */}
      <div className="border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b">
                <th className="text-left px-4 py-3 font-medium text-gray-600">Name</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Email</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">IRATA Level</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Employment</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Certs</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Cert Status</th>
              </tr>
            </thead>
            <tbody>
                {isLoading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i} className="border-b animate-pulse">
                      <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-28" /></td>
                      <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-36" /></td>
                      <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-20" /></td>
                      <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-16" /></td>
                      <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-8" /></td>
                      <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-20" /></td>
                    </tr>
                  ))
                ) : !data?.results?.length ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-16 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <div className="flex items-center justify-center w-12 h-12 rounded-full bg-gray-100">
                          <Users className="h-6 w-6 text-gray-400" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">No employees yet</p>
                          <p className="text-sm text-gray-500 mt-1">Invite your first team member to get started.</p>
                        </div>
                        <button
                          onClick={() => setInviteOpen(true)}
                          className="mt-2 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          <Plus className="h-4 w-4" />
                          Invite Employee
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : (
                data.results.map((tech) => {
                  const certStatus = getCertStatusDisplay(tech);
                  return (
                    <tr
                      key={tech.id}
                      onClick={() => handleRowClick(tech)}
                      className="border-b hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <td className="px-4 py-3 font-medium text-gray-900">
                        {tech.first_name} {tech.last_name}
                      </td>
                      <td className="px-4 py-3 text-gray-600">{tech.email}</td>
                      <td className="px-4 py-3 text-gray-600">
                        {getIrataLevel(tech)}
                      </td>
                      <td className="px-4 py-3">
                        <Badge
                          className={cn(
                            'capitalize',
                            statusColors[tech.status] || 'bg-gray-100 text-gray-800'
                          )}
                        >
                          {tech.status?.replace('_', ' ')}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        {tech.certification_summary?.length ?? 0}
                      </td>
                      <td className="px-4 py-3">
                        <span className={cn('inline-flex items-center gap-1', certStatus.className)}>
                          {certStatus.label !== 'Valid' && certStatus.label !== 'None' && (
                            <AlertTriangle className="h-3.5 w-3.5" />
                          )}
                          {certStatus.label}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {data && (data.next || data.previous) && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-600">
            {data.count} total result{data.count !== 1 ? 's' : ''}
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={!data.previous}
              className="px-3 py-1.5 text-sm border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
            >
              Previous
            </button>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={!data.next}
              className="px-3 py-1.5 text-sm border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Invite Modal */}
      <InviteEmployeeModal open={inviteOpen} onOpenChange={setInviteOpen} />
    </div>
  );
}

// ─── Invite Employee Modal ──────────────────────────────────────────────────

function InviteEmployeeModal({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [selectedProjects, setSelectedProjects] = useState<number[]>([]);
  const [moduleRoles, setModuleRoles] = useState<Record<string, ModuleRoleLevel>>({});

  const { enabledModules } = usePlatformModules();
  const { data: projectsData } = useProjects({ page_size: 100 });
  const inviteMutation = useAdminInvite();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const moduleRolesPayload = Object.entries(moduleRoles)
      .filter(([, role]) => role !== 'no_access')
      .map(([module, role]) => ({ module, role }));

    await inviteMutation.mutateAsync({
      email,
      first_name: firstName,
      last_name: lastName,
      role: 'Viewer',
      project_ids: selectedProjects.length > 0 ? selectedProjects : undefined,
      module_roles: moduleRolesPayload.length > 0 ? moduleRolesPayload : undefined,
    });

    setEmail('');
    setFirstName('');
    setLastName('');
    setSelectedProjects([]);
    setModuleRoles({});
    onOpenChange(false);
  };

  const toggleProject = (id: number) => {
    setSelectedProjects((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Invite Employee</DialogTitle>
          <DialogDescription>
            Send an invitation to join your organization.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                First Name
              </label>
              <input
                type="text"
                required
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Last Name
              </label>
              <input
                type="text"
                required
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Assign to Jobs */}
          {projectsData?.results && projectsData.results.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Assign to Jobs
              </label>
              <div className="max-h-32 overflow-y-auto border rounded-lg p-2 space-y-1">
                {projectsData.results.map((project) => (
                  <label
                    key={project.id}
                    className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 px-2 py-1 rounded"
                  >
                    <input
                      type="checkbox"
                      checked={selectedProjects.includes(project.id)}
                      onChange={() => toggleProject(project.id)}
                      className="rounded border-gray-300"
                    />
                    <span>{project.name}</span>
                    {project.project_number && (
                      <span className="text-gray-400">({project.project_number})</span>
                    )}
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Per-module roles */}
          {enabledModules.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Module Roles
              </label>
              <div className="space-y-2">
                {enabledModules.map((mod) => (
                  <div key={mod} className="flex items-center justify-between">
                    <span className="text-sm capitalize">{mod}</span>
                    <Select
                      value={moduleRoles[mod] || 'no_access'}
                      onValueChange={(v) =>
                        setModuleRoles((prev) => ({ ...prev, [mod]: v as ModuleRoleLevel }))
                      }
                    >
                      <SelectTrigger className="w-[140px]" size="sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="no_access">No Access</SelectItem>
                        <SelectItem value="viewer">Viewer</SelectItem>
                        <SelectItem value="inspector">Inspector</SelectItem>
                        <SelectItem value="manager">Manager</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </div>
          )}

          <DialogFooter>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={inviteMutation.isPending}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {inviteMutation.isPending ? 'Sending...' : 'Send Invitation'}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
